/**
 * Spawns that do not exist until the mission calls for them: mission 5's minitanks wake when
 * the ranger nears the pumping station, mission 10's executioners when the prison is opened.
 * Also the two elites that `reinforceTarget` puts on a target.
 */

import { sendToInvestigate } from '../alert';
import type { Ctx } from '../ctx';
import { removeProp } from '../damage';
import { createEnemy } from '../enemies/create';
import { PI, TAU, cos, sin } from '../math';
import { resolveMovement } from '../statics';
import { ENEMY, OBJECTIVES } from '../tuning';
import type { Enemy, PropDef, SpawnDef } from '../types';
import { dist, targetProps } from './common';

function spawn(ctx: Ctx, def: SpawnDef): Enemy {
  const w = ctx.world;
  const e = createEnemy(w.nextId++, def, w.enemies.length, ctx.rng.float(), ctx.rng.float(), ctx.statics.def.threat);
  w.enemies.push(e);
  return e;
}

function wake(ctx: Ctx, trigger: 'nearObjective' | 'objectiveOpened'): Enemy[] {
  const out: Enemy[] = [];
  for (const s of ctx.statics.def.spawns) if (s.dormantUntil === trigger) out.push(spawn(ctx, s));
  return out;
}

/**
 * A `timedEvent` has fired: the spawns waiting on its tag appear where they were placed (10 §3). If the
 * mission's `objectiveTarget` is among them he is known from now on - unless he has already turned back, and
 * then his whole party stays home with him.
 */
/**
 * Is this the tag of the objective target's own party, and has he been warned off (`targetFlees` before he set
 * out)? Then nobody of it comes: an escort does not walk the road without the man it is escorting.
 */
export function partyStaysHome(ctx: Pick<Ctx, 'world' | 'statics'>, tag: string): boolean {
  if (!ctx.world.objective.targetGone) return false;
  return ctx.statics.def.spawns
    .some((s) => (s.objectiveTarget === true || s.carriesTarget === true)
      && s.dormantUntil === 'timedEvent' && (s.wakeTag ?? '') === tag);
}

export function wakeTagged(ctx: Ctx, tag: string): Enemy[] {
  const o = ctx.world.objective;
  const out: Enemy[] = [];
  if (partyStaysHome(ctx, tag)) return out;
  for (const s of ctx.statics.def.spawns) {
    if (s.dormantUntil !== 'timedEvent' || (s.wakeTag ?? '') !== tag) continue;
    if (s.objectiveTarget === true && o.targetGone) continue;
    // R4: a spawn that IS a parked prop under way. A wreck drives nowhere; one that still stands leaves its
    // hardstanding empty the moment it pulls off it.
    if (s.replacesPropId !== undefined) {
      const state = ctx.world.props.find((p) => p.id === s.replacesPropId);
      if (!state || state.destroyed) continue;
      removeProp(ctx, s.replacesPropId);
    }
    const e = spawn(ctx, s);
    e.wokenBy = tag;
    if (s.objectiveTarget === true) o.targetEnemyId = e.id;
    // R4: he is riding. Until the ranger stops the vehicle, the vehicle is where the mission's man is.
    if (s.carriesTarget === true) o.targetAboardId = e.id;
    out.push(e);
  }
  return out;
}

export function stepDormant(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const def = statics.def;
  const r = w.ranger;

  if (!o.woken.nearObjective) {
    // No target props (a zone objective): the middle of the zone stands in for them.
    const zone = def.objective.zone;
    const points: Array<[number, number]> = targetProps(def).map((p) => [p.x, p.z]);
    if (points.length === 0 && zone) points.push([(zone.x0 + zone.x1) / 2, (zone.z0 + zone.z1) / 2]);
    if (points.some(([x, z]) => dist(x, z, r.x, r.z) <= OBJECTIVES.wakeRadius)) {
      o.woken.nearObjective = true;
      wake(ctx, 'nearObjective');
    }
  }

  if (!o.woken.objectiveOpened && o.opened) {
    o.woken.objectiveOpened = true;
    // They come for the people who have just been let out (03 §2, mission 10).
    const prey = o.captives.find((c) => c.state === 'following') ?? o.captives[0];
    for (const e of wake(ctx, 'objectiveOpened')) {
      e.wary = true;
      if (prey) sendToInvestigate(e, prey.x, prey.z, true);
    }
  }
}

/** `reinforceTarget`: elites take post around the target, facing outward. */
export function reinforceTarget(ctx: Ctx, target: PropDef): void {
  const count = OBJECTIVES.reinforceCount;
  for (let i = 0; i < count; i++) {
    const a = PI / 2 + (TAU * i) / count;
    const ring = OBJECTIVES.reinforceRing;
    const spot = resolveMovement(ctx.statics, target.x + sin(a) * ring, target.z + cos(a) * ring, ENEMY.radius);
    const e = spawn(ctx, { kind: 'elite', x: spot.x, z: spot.z, facing: a });
    e.wary = true;
  }
  ctx.events.push({ type: 'reinforcements', count });
}
